import { HttpResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormArray,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { isNil, omit, pull, without } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import {
  COUNTRY_DIAL_CODES,
  CountryDialCodeDto,
  MINUTES_IN_HOUR,
  MouseUserEvent,
  Permission,
  TruckSize,
} from '../constants';
import { NOT_AVAILABLE } from '../constants/template.constant';
import { AddressModel, ObjectWithoutEmptyValues, PermissionModel } from '../models';
import { MatSelect } from '@angular/material/select';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AddressDetailsModel } from '../../store';

export function parseQuery(queryString: string): { [key: string]: unknown } {
  const query = {};
  const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
  for (const value of pairs) {
    const pair = value.split('=');
    query[pair[0]] = pair[1] || '';
  }
  return query;
}

export function windowOpenUrl(url: string): void {
  if (!url.match(/^https?:\/\//i)) {
    url = 'http://' + url;
  }
  window.open(url);
}

export function flatDeepNestedCollection<ArrayItemType extends { items: unknown[] }>(
  array: ArrayItemType[],
): Omit<ArrayItemType, 'items'>[] {
  {
    return array.reduce((r: Omit<ArrayItemType, 'items'>[], { items, ...rest }: ArrayItemType) => {
      r.push(rest);
      if (items) {
        r.push(...flatDeepNestedCollection(items as ArrayItemType[]));
      }
      return r;
    }, []);
  }
}

export function getQueryStringFromObject<ParamsType>(
  params: { [key in keyof ParamsType]: unknown },
  exclude?: string | string[],
): string {
  const queryString =
    params &&
    Object.keys(exclude ? omit(params, exclude) : params)
      .filter((key: string) => {
        let paramKey = '';
        const paramsValue = params[key];

        if (Array.isArray(paramsValue)) {
          paramKey = paramsValue.length ? key : null;
        } else {
          paramKey = !isNil(paramsValue) && paramsValue !== '' ? key : null;
        }
        return paramKey;
      })
      .map((key: string) => key + '=' + encodeURIComponent(params[key]))
      .join('&');
  return queryString;
}

export function getDuplicateValuesFromArray<ArrayItemType>(array: ArrayItemType[]): ArrayItemType[] {
  return array.reduce(
    (acc: ArrayItemType[], v: ArrayItemType, i: number, arr: ArrayItemType[]) =>
      arr.indexOf(v) !== i && acc.indexOf(v) === -1 ? acc.concat(v) : acc,
    [],
  );
}

export function markAllFormControlsAsTouched(group: UntypedFormGroup): void {
  for (const control in group.controls) {
    if (group.controls.hasOwnProperty(control)) {
      group.get(control).markAsTouched();
    }
  }
}

export function setStateAllFormControls(group: UntypedFormGroup, isDisabled: boolean): void {
  for (const control in group.controls) {
    if (group.controls.hasOwnProperty(control)) {
      const c = group.get(control);
      isDisabled ? c.disable() : c.enable();
    }
  }
}

export function markRequiredControlWithAsteriskSymbol(control: UntypedFormControl | AbstractControl): string {
  if (!control.validator) {
    return '';
  }
  const validator = control.validator({} as AbstractControl);

  return validator && validator.required ? '*' : '';
}

export function isControlRequired(control: UntypedFormControl | AbstractControl): boolean {
  if (!control.validator) {
    return;
  }

  const validator = control.validator({} as AbstractControl);

  return validator?.required || false;
}

export function markFormArrayAsTouched(formArray: UntypedFormArray): void {
  formArray.controls.forEach((group: UntypedFormGroup) => markAllFormControlsAsTouched(group));
}

export function changeCheckbox(control: UntypedFormControl, { checked, source: { value } }: MatCheckboxChange): void {
  checked ? control.setValue([...control.value, value]) : control.setValue(pull(control.value, value));
}

export function joinByDelimiter<T>(data: T[], delimiter: string = '.'): string {
  return data.join(delimiter);
}

export function mapNullToNotAvailableValue<ValueType>(data: ValueType): {
  [key in keyof ValueType]: NonNullable<ValueType[key]>;
} {
  return Object.entries(data).reduce((acc: ValueType, [key, value]: [string, unknown | null]) => {
    return {
      ...acc,
      [key]: !isNil(value) ? value : NOT_AVAILABLE,
    };
  }, {} as ValueType);
}

export function replaceSlash(value: string): string {
  if (value && (value.includes('/') || value.includes(`\\`))) {
    return value.replace(/\//g, '%2F').replace(/\\/g, '%5C');
  } else {
    return value;
  }
}

export function parseChipsInputBulkDataEntry(
  control: UntypedFormControl | AbstractControl,
  input: HTMLInputElement,
  inputControl: UntypedFormControl,
  splitPattern?: RegExp,
  shouldSplitWithSpace?: boolean,
  removeDuplicates?: boolean,
): void {
  const { value } = inputControl;

  if (removeDuplicates && control?.value?.includes(value)) {
    inputControl.setValue(null);
    input.value = '';

    return;
  }

  if (value && typeof value === 'string' && value.trim()) {
    shouldBeSplit(value, control, splitPattern, shouldSplitWithSpace);
    control.markAsDirty();
  }

  if (input) {
    input.value = '';
  }

  inputControl.setValue(null);
}

export function shouldBeSplit(
  value: string,
  control: UntypedFormControl | AbstractControl,
  pattern?: RegExp,
  shouldSplitWithSpace?: boolean,
): void {
  if (shouldSplitWithSpace === false) {
    const newVal = [value.trim()];
    control.value ? control.setValue(control.value.concat(newVal)) : control.setValue(newVal);
  } else {
    const splitValue = pattern ? value?.split(pattern) : value?.split(/[\s,`:'"?;]+/);
    const newVal = splitValue.filter((v: string) => (v || '').trim());
    control.value ? control.setValue(control.value.concat(newVal)) : control.setValue(newVal);
  }
}

export function removeInputElement(control: UntypedFormControl | AbstractControl, element: string): void {
  control.setValue(without(control.value, element));
  control.markAsDirty();
}

export function assignObjectIfPropertyValueValid<T>(target: T, params: T): void {
  Object.keys(params).forEach((key: string) => {
    if (!isNil(params[key])) {
      target[key] = params[key];
    }
  });
}

export function stopPropagation(e: Event): void {
  e.stopPropagation();
}

export function getClosestValueInArray(array: number[], target: number): number {
  return array.sort((a: number, b: number) => Math.abs(target - a) - Math.abs(target - b))[0];
}

export function getFormGroup(formGroup: AbstractControl): UntypedFormGroup {
  return formGroup as UntypedFormGroup;
}

export function getFormControl(formControl: AbstractControl): UntypedFormControl {
  return formControl as UntypedFormControl;
}

export function checkboxListRequiredValidator(formArray: UntypedFormArray): ValidationErrors | null {
  return formArray.controls?.some((control: AbstractControl) => control.value)
    ? null
    : {
        required: true,
      };
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;

  document.body.append(link);
  link.click();
  link.remove();
}

export function previewBlob(blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  window.open(url);
}

export function getBlobFromPDF(pdf: Blob): Blob {
  return new Blob([pdf], { type: 'application/pdf' });
}

export function getFileNameFromContentDispositionHeader(response: HttpResponse<Blob>): string {
  return response.headers.get('content-disposition').split('filename="')[1].split('"')[0];
}

export function tapOnce<T>(fn: (v: T) => void): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>) => {
    source
      .pipe(
        first(),
        tap((value: T) => fn(value)),
      )
      .subscribe();

    return source;
  };
}

export function findLowestNumberNotInList(initialNumber: number, list: number[]): number {
  return list.indexOf(initialNumber) < 0 ? initialNumber : findLowestNumberNotInList(initialNumber + 1, list);
}

export function multipleUnsubscribe(subscriptions: Subscription[]): void {
  if (!subscriptions) {
    return;
  }

  subscriptions
    .filter(Boolean)
    .filter((props: Subscription) => props.unsubscribe && typeof props.unsubscribe === 'function')
    .forEach((props: Subscription) => props.unsubscribe());
}

export function trimFormValueOnTopLevel<T>(obj: T): T {
  Object.keys(obj).map((key: string) => (obj[key] = typeof obj[key] === 'string' ? obj[key].trim() : obj[key]));
  return obj;
}

export function alphanumericSortByName<T>(listItems: T[]): T[] {
  const orderType = 'name';
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'accent',
    localeMatcher: 'best fit',
  });
  return listItems.sort((a: T, b: T) => collator.compare(a[orderType], b[orderType]));
}

export function twoLevelAlphanumericSort<T>(listToBeSorted: T[], orderType1: string, orderType2: string): T[] {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'accent',
    localeMatcher: 'best fit',
  });
  return listToBeSorted.sort((a: T, b: T) => {
    if (a[orderType1] === b[orderType1]) {
      return collator.compare(a[orderType2], b[orderType2]);
    } else {
      return collator.compare(a[orderType1], b[orderType1]);
    }
  });
}

export function removeFormGroupControlValidator(formGroup: UntypedFormGroup): void {
  for (const key of Object.keys(formGroup.controls)) {
    formGroup.controls[key].clearValidators();
    formGroup.controls[key].updateValueAndValidity();
  }
}

export function transformPermissionArrayToObject(array: PermissionModel[]): Record<Permission, boolean> {
  return array.reduce(
    (accum: Record<Permission, boolean>, { items, itemName }: { items: PermissionModel[]; itemName: string }) => {
      if (items) {
        accum[itemName] = transformPermissionArrayToObject(items);
      } else {
        accum[itemName] = true;
      }
      return accum;
    },
    {} as Record<Permission, boolean>,
  );
}

export function convertStatusClassNames(className: string): string {
  return className.replace(/_/g, '-');
}

export function removeEmptyValueFromObj(obj: ObjectWithoutEmptyValues): { [key: string]: unknown } {
  return Object.entries(obj)
    .filter(([_objKey, value]: [string, unknown]) => !isNil(value) && value !== '')
    .reduce((acc: ObjectWithoutEmptyValues, [key, value]: [string, unknown]) => ({ ...acc, [key]: value }), {});
}

export function sortByTruckSize(a: TruckSize, b: TruckSize): number {
  const sortingArray = Object.values(TruckSize);
  return sortingArray.indexOf(a) - sortingArray.indexOf(b);
}

export function checkSupportedCountryCode(phoneValue: string, supportedCountryCodes: string[]): boolean {
  const dialCodeValue: string = phoneValue
    ? phoneValue.substring(phoneValue.indexOf('+') + 1, phoneValue.indexOf(' '))
    : '';
  const countryDialCodes: string[] = COUNTRY_DIAL_CODES.filter(
    (item: CountryDialCodeDto) => item.dialCodeValue === dialCodeValue,
  ).map((item: CountryDialCodeDto) => item.countryDialCode);

  return countryDialCodes.some((code: string) => supportedCountryCodes.includes(code));
}

export function closeDropdown(opened: boolean, select: MatSelect): void {
  if (opened) {
    select.panel.nativeElement.addEventListener(`${MouseUserEvent.Mouseleave}`, () => {
      select.close();
    });
  }
}

export function unchangedForm<FormType>(initialValue: FormType, finalValue: FormType): boolean {
  if (initialValue instanceof Array && finalValue instanceof Array) {
    return JSON.stringify(initialValue.sort()) === JSON.stringify(finalValue.sort());
  }
  return JSON.stringify(initialValue) === JSON.stringify(finalValue);
}

export function unchangedFormArray<FormArray>(initialValue: FormArray, finalValue: FormArray): boolean {
  if (initialValue instanceof FormArray && finalValue instanceof FormArray) {
    if (initialValue.length !== finalValue.length) {
      return false;
    }

    for (let i = 0; i < initialValue.length; i++) {
      const initialControl = initialValue.at(i);
      const finalControl = finalValue.at(i);

      if (JSON.stringify(initialControl.value) !== JSON.stringify(finalControl.value)) {
        return false;
      }
    }

    return true;
  }
}

export const searchByOptionalParam = <T>(searchStr: string, dataToSearchFrom: T[], dataParam?: string): T[] => {
  const searchResult = dataToSearchFrom?.filter((data: T) =>
    (dataParam ? data[dataParam] : data)?.toLocaleLowerCase().includes(searchStr.toLocaleLowerCase()),
  );

  return searchResult?.length ? searchResult : [];
};

export function compareAddressName(
  a: AddressDetailsModel | AddressModel,
  b: AddressDetailsModel | AddressModel,
): boolean {
  return a?.name === b?.name;
}

export function isEqualAddresses(a: AddressModel, b: AddressModel): boolean {
  return a.addressExtId === b.addressExtId && a.name === b.name && a.fullAddress === b.fullAddress;
}

export function clearValidatorsAndUpdate(control: AbstractControl): void {
  control.clearValidators();
  control.updateValueAndValidity();
}

export function addRequiredValidatorAndUpdate(control: AbstractControl): void {
  control.setValidators(Validators.required);
  control.updateValueAndValidity();
}

export function markAsTouchedAndUpdate(control: AbstractControl): void {
  control.markAsTouched();
  control.updateValueAndValidity();
}

export function getRoundedMinutes(seconds: number): number {
  const roundedMinutes = Math.ceil(seconds / MINUTES_IN_HOUR);
  return roundedMinutes !== 0 ? roundedMinutes : 1;
}
